import './PrimaryToolTip.pcss';
import React, {
  ReactNode,
  useState,
  useCallback,
  useEffect,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react';
import Popover, { PopoverProps } from 'react-tiny-popover';
import { isFunction, isBoolean, isNil } from 'lodash';
import classnames from 'classnames';
import {
  ColorType,
  getColorStylesByType,
} from '../../../editor/features/beta/styles/colorsByType';
import { colors } from '../../../editor/features/beta/styles/colors';

type ChildrenRenderProp = (closeToolTip?: () => void) => ReactNode;

type Props = {
  title?: string;
  children?: ChildrenRenderProp | ReactNode | Element | string;
  anchorElement: ReactNode;
  position?: 'top' | 'bottom' | 'right' | 'left';
  align?: 'start' | 'center' | 'end';
  classNameTitle?: string;
  classNameContainer?: string;
  classNameContent?: string;
  classNamePopoverContainer?: string;
  classNameArrow?: string;
  useHover?: boolean;
  toolTipDisabled?: boolean;
  isVisible?: boolean;
  enableReposition?: boolean;
  withoutLine?: boolean;
  containerStyle?: PopoverProps['containerStyle'];
  contentLocation?: PopoverProps['contentLocation'];
  setIsVisible?: Dispatch<SetStateAction<boolean>>;
  type?: ColorType;
  zIndex?: number;
};

const HOVER_CLOSE_DELAY_MS = 300;

export const PrimaryToolTip = (props: Props) => {
  const {
    toolTipDisabled,
    anchorElement,
    containerStyle,
    contentLocation,
    title,
    children,
    classNameTitle,
    classNamePopoverContainer,
    classNameContainer,
    classNameContent,
    useHover = false,
    position = 'bottom',
    align = 'center',
    isVisible,
    enableReposition,
    classNameArrow,
    withoutLine = false,
    setIsVisible,
    type = 'info',
    zIndex = '1000002',
  } = props;
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const localTooltipVisible = isNil(isVisible) ? tooltipVisible : isVisible;
  const setLocalTooltipVisible = setIsVisible
    ? setIsVisible
    : setTooltipVisible;

  useEffect(() => {
    if (isBoolean(isVisible)) {
      setLocalTooltipVisible(isVisible);
    }
  }, [isVisible]);

  const { color } = getColorStylesByType(type);
  const style = {
    backgroundColor: type === 'info' ? colors.accentBlue : color,
  };

  const titleStyles = classnames(
    'primary-tool-tip__tip-area-title',
    classNameTitle,
  );

  const containerStyles = classnames(
    'primary-tool-tip__tip-area',
    classNameContainer,
  );
  const lineStyles = classnames(
    'primary-tool-tip__tip-area-bottom-line',
    `primary-tool-tip__tip-area-bottom-line--${position}`,
  );
  const arrowStyles = classnames(
    `primary-tool-tip__tip-area-arrow-icon`,
    `primary-tool-tip__tip-area-arrow--${position}`,
    `primary-tool-tip__tip-area-arrow--align-${align}`,
    classNameArrow,
  );
  const contentStyles = classnames('primary-tool-tip', classNameContent);

  const popoverClassName = classnames(
    'react-tiny-popover-container',
    classNamePopoverContainer,
    {
      'primary-tool-tip__tip-area-use-hover': useHover,
    },
  );

  const memoizedCloseToolTip = useCallback(() => {
    setLocalTooltipVisible(false);
  }, [setLocalTooltipVisible]);

  const memoizedOnClickOutside = useCallback(
    (e: MouseEvent) => {
      const isInsideClick = e.composedPath().some((target: Element) => {
        return target.className === 'react-tiny-popover-container';
      });
      if (!isInsideClick) {
        setLocalTooltipVisible(false);
      }
    },
    [setLocalTooltipVisible],
  );

  const memoizedOpenToolTip = useCallback(() => {
    setLocalTooltipVisible(true);
  }, [setLocalTooltipVisible]);

  const memoizedOnClickHandler = useCallback(() => {
    setLocalTooltipVisible(prevTooltipVisible => !prevTooltipVisible);
  }, [setLocalTooltipVisible]);

  const closeTimeoutRef = useRef<number | null>(null);

  const onMouseLeave = () => {
    closeTimeoutRef.current = window?.setTimeout(
      memoizedCloseToolTip,
      HOVER_CLOSE_DELAY_MS,
    );
  };

  const onMouseEnter = () => {
    if (closeTimeoutRef.current) {
      window?.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (!isVisible) {
      memoizedOpenToolTip();
    }
  };

  const propsForHoverHandling = { onMouseEnter, onMouseLeave };

  const propsForClickHandling = {
    onClick: memoizedOnClickHandler,
  };

  const handlingPropsAnchor = toolTipDisabled
    ? {}
    : useHover
    ? propsForHoverHandling
    : propsForClickHandling;

  const handlingPropsContent = useHover ? propsForHoverHandling : {};

  return (
    <Popover
      disableReposition={!enableReposition}
      align={align}
      position={position}
      isOpen={localTooltipVisible && !toolTipDisabled}
      onClickOutside={memoizedOnClickOutside}
      containerClassName={popoverClassName}
      containerStyle={{ zIndex: zIndex.toString(), ...containerStyle }}
      contentLocation={contentLocation}
      content={() => (
        <div className={containerStyles} {...handlingPropsContent}>
          <div className={arrowStyles} />

          {title && <div className={titleStyles}>{title}</div>}

          <div className="primary-tool-tip__tip-area-body">
            {isFunction(children) ? children(memoizedCloseToolTip) : children}
          </div>

          {!withoutLine && <div className={lineStyles} style={style} />}
        </div>
      )}
    >
      <div className={contentStyles} {...handlingPropsAnchor}>
        {anchorElement}
      </div>
    </Popover>
  );
};
